import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  FileSpreadsheet, FileText, Download, Calendar,
  Users, Activity, Droplet, CheckCircle, Loader2,
  Plus, Clock, Mail, Trash2, Play, Settings
} from "lucide-react";
import { toast } from "sonner";

const PartnerReports = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [patients, setPatients] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [newReport, setNewReport] = useState({
    frequency: "weekly",
    day_of_week: 0,
    day_of_month: 1,
    recipient_emails: "",
    include_patient_details: true,
    include_compliance_summary: true,
    include_hba1c_trends: true
  });

  useEffect(() => {
    fetchData();
    fetchScheduledReports();
  }, [token]);

  const fetchData = async () => {
    try {
      // Fetch overview
      const overviewRes = await fetch(`${API}/partner-admin/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data);
      }

      // Fetch all patients
      const patientsRes = await fetch(`${API}/partner-admin/patients?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (patientsRes.ok) {
        const data = await patientsRes.json();
        setPatients(data.patients || []);
      }
    } catch (error) {
      console.error("Fetch data error:", error);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const response = await fetch(`${API}/partner-admin/scheduled-reports`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledReports(data.reports || []);
      }
    } catch (error) {
      console.error("Fetch scheduled reports error:", error);
    }
  };

  const createScheduledReport = async () => {
    const emails = newReport.recipient_emails.split(",").map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      toast.error("Please enter at least one email address");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/partner-admin/scheduled-reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          ...newReport,
          recipient_emails: emails,
          day_of_week: newReport.frequency === "weekly" ? newReport.day_of_week : null,
          day_of_month: newReport.frequency === "monthly" ? newReport.day_of_month : null,
        }),
      });

      if (response.ok) {
        toast.success("Scheduled report created successfully");
        setShowScheduleDialog(false);
        setNewReport({
          frequency: "weekly",
          day_of_week: 0,
          day_of_month: 1,
          recipient_emails: "",
          include_patient_details: true,
          include_compliance_summary: true,
          include_hba1c_trends: true
        });
        fetchScheduledReports();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create scheduled report");
      }
    } catch (error) {
      toast.error("Failed to create scheduled report");
    } finally {
      setLoading(false);
    }
  };

  const deleteScheduledReport = async (reportId) => {
    if (!confirm("Are you sure you want to delete this scheduled report?")) return;

    try {
      const response = await fetch(`${API}/partner-admin/scheduled-reports/${reportId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        toast.success("Scheduled report deleted");
        fetchScheduledReports();
      } else {
        toast.error("Failed to delete scheduled report");
      }
    } catch (error) {
      toast.error("Failed to delete scheduled report");
    }
  };

  const sendReportNow = async (reportId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/partner-admin/scheduled-reports/${reportId}/send-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Report sent to ${data.recipients.join(", ")}`);
        fetchScheduledReports();
      } else {
        toast.error("Failed to send report");
      }
    } catch (error) {
      toast.error("Failed to send report");
    } finally {
      setLoading(false);
    }
  };

  const generateCSV = () => {
    setLoading(true);
    
    try {
      // Build CSV content
      const headers = [
        "Patient Name",
        "Email",
        "Compliance Score",
        "Compliance Level",
        "Estimated HbA1c",
        "Last Activity",
        "Status"
      ];

      const rows = patients.map((p) => {
        const complianceLevel = p.compliance_score >= 71 ? "Good" : 
                               p.compliance_score >= 41 ? "Moderate" : "Poor";
        const lastActivity = p.last_activity 
          ? new Date(p.last_activity).toLocaleDateString() 
          : "Never";
        const isActive = p.last_activity && 
          (new Date() - new Date(p.last_activity)) / (1000 * 60 * 60 * 24) < 7;
        
        return [
          p.name || "Unknown",
          p.email || "",
          p.compliance_score || 0,
          complianceLevel,
          p.estimated_hba1c ? `${p.estimated_hba1c}%` : "N/A",
          lastActivity,
          isActive ? "Active" : "Inactive"
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `platemind_patient_report_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("CSV report downloaded successfully");
    } catch (error) {
      console.error("CSV generation error:", error);
      toast.error("Failed to generate CSV report");
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    setLoading(true);
    
    try {
      // Build PDF content (simple HTML that can be printed as PDF)
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>PlateMind Patient Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            h1 { color: #F97316; margin-bottom: 10px; }
            .subtitle { color: #666; margin-bottom: 30px; }
            .stats { display: flex; gap: 20px; margin-bottom: 30px; }
            .stat { background: #f5f5f5; padding: 15px 20px; border-radius: 8px; }
            .stat-value { font-size: 24px; font-weight: bold; color: #333; }
            .stat-label { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #F97316; color: white; padding: 12px; text-align: left; }
            td { padding: 10px 12px; border-bottom: 1px solid #eee; }
            tr:nth-child(even) { background: #f9f9f9; }
            .good { color: #22c55e; }
            .moderate { color: #f59e0b; }
            .poor { color: #ef4444; }
            .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>PlateMind Patient Report</h1>
          <p class="subtitle">${overview?.partner?.organization_name || "Organization"} | Generated: ${new Date().toLocaleDateString()}</p>
          
          <div class="stats">
            <div class="stat">
              <div class="stat-value">${overview?.total_patients || 0}</div>
              <div class="stat-label">Total Patients</div>
            </div>
            <div class="stat">
              <div class="stat-value">${overview?.active_patients || 0}</div>
              <div class="stat-label">Active (7 days)</div>
            </div>
            <div class="stat">
              <div class="stat-value">${overview?.high_compliance || 0}</div>
              <div class="stat-label">High Compliance</div>
            </div>
            <div class="stat">
              <div class="stat-value">${overview?.average_hba1c ? overview.average_hba1c + "%" : "N/A"}</div>
              <div class="stat-label">Avg HbA1c</div>
            </div>
          </div>

          <h2>Patient Details</h2>
          <table>
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Email</th>
                <th>Compliance</th>
                <th>Est. HbA1c</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${patients.map(p => {
                const complianceClass = p.compliance_score >= 71 ? "good" : 
                                       p.compliance_score >= 41 ? "moderate" : "poor";
                const isActive = p.last_activity && 
                  (new Date() - new Date(p.last_activity)) / (1000 * 60 * 60 * 24) < 7;
                return `
                  <tr>
                    <td>${p.name || "Unknown"}</td>
                    <td>${p.email || ""}</td>
                    <td class="${complianceClass}">${p.compliance_score || 0}%</td>
                    <td>${p.estimated_hba1c ? p.estimated_hba1c + "%" : "N/A"}</td>
                    <td>${isActive ? "Active" : "Inactive"}</td>
                  </tr>
                `;
              }).join("")}
            </tbody>
          </table>

          <div class="footer">
            <p>Generated by PlateMind | Confidential Medical Report</p>
          </div>
        </body>
        </html>
      `;

      // Open print dialog for PDF
      const printWindow = window.open("", "_blank");
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Small delay to ensure content is loaded
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success("PDF report ready - use Print dialog to save as PDF");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary">Reports</h1>
        <p className="text-muted-foreground">Export patient data and compliance reports</p>
      </div>

      {/* Summary Cards - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => navigate('/partner/patients?filter=all')}
          data-testid="stat-total-patients"
        >
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-secondary">{overview?.total_patients || 0}</p>
              <p className="text-xs text-muted-foreground">Total Patients</p>
            </div>
          </div>
        </Card>
        <Card 
          className="p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => navigate('/partner/patients?filter=active')}
          data-testid="stat-active-users"
        >
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-secondary">{overview?.active_patients || 0}</p>
              <p className="text-xs text-muted-foreground">Active Users</p>
            </div>
          </div>
        </Card>
        <Card 
          className="p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => navigate('/partner/patients?filter=good')}
          data-testid="stat-high-compliance"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-success" />
            <div>
              <p className="text-2xl font-bold text-success">{overview?.high_compliance || 0}</p>
              <p className="text-xs text-muted-foreground">High Compliance</p>
            </div>
          </div>
        </Card>
        <Card 
          className="p-4 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => navigate('/partner/patients?filter=hba1c')}
          data-testid="stat-avg-hba1c"
        >
          <div className="flex items-center gap-3">
            <Droplet className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-secondary">
                {overview?.average_hba1c ? `${overview.average_hba1c}%` : "N/A"}
              </p>
              <p className="text-xs text-muted-foreground">Avg HbA1c</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Export Options */}
      <Card className="p-6 rounded-2xl border-orange-100">
        <h2 className="font-bold text-secondary mb-4">Export Reports</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* CSV Export */}
          <div className="p-6 border border-orange-100 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-secondary">CSV Export</h3>
                <p className="text-sm text-muted-foreground">Spreadsheet format for data analysis</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Export patient compliance data, HbA1c estimates, and activity status in CSV format. 
              Perfect for importing into Excel or Google Sheets.
            </p>
            <Button 
              onClick={generateCSV}
              disabled={loading || patients.length === 0}
              className="w-full rounded-full bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download CSV
            </Button>
          </div>

          {/* PDF Export */}
          <div className="p-6 border border-orange-100 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-secondary">PDF Report</h3>
                <p className="text-sm text-muted-foreground">Formatted report for sharing</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Generate a formatted PDF report with organization summary, compliance breakdown, 
              and patient details. Ideal for presentations and official records.
            </p>
            <Button 
              onClick={generatePDF}
              disabled={loading || patients.length === 0}
              variant="outline"
              className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Generate PDF
            </Button>
          </div>
        </div>

        {/* Report Contents */}
        <div className="mt-6 p-4 bg-orange-50/50 rounded-xl">
          <h4 className="font-medium text-secondary mb-2">Report Contents</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Patient names and contact information</li>
            <li>• Compliance scores and compliance levels (Good/Moderate/Poor)</li>
            <li>• Estimated HbA1c values</li>
            <li>• Last activity dates and active status</li>
            <li>• Organization summary statistics</li>
          </ul>
        </div>
      </Card>

      {/* Coming Soon */}
      <Card className="p-6 rounded-2xl border-orange-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-green-600" />
            <h3 className="font-bold text-secondary">Scheduled Reports</h3>
          </div>
          <Button 
            onClick={() => setShowScheduleDialog(true)}
            className="rounded-full bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule Report
          </Button>
        </div>
        
        {scheduledReports.length > 0 ? (
          <div className="space-y-3">
            {scheduledReports.map((report) => (
              <div 
                key={report.report_id} 
                className="flex items-center justify-between p-4 border border-orange-100 rounded-xl"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    report.is_active ? "bg-green-100" : "bg-gray-100"
                  }`}>
                    <Clock className={`w-5 h-5 ${report.is_active ? "text-green-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <p className="font-medium text-secondary capitalize">
                      {report.frequency} Report
                      {report.frequency === "weekly" && ` (${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][report.day_of_week]})`}
                      {report.frequency === "monthly" && ` (Day ${report.day_of_month})`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Mail className="w-3 h-3 inline mr-1" />
                      {report.recipient_emails?.join(", ")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Next: {report.next_run ? new Date(report.next_run).toLocaleDateString() : "Not scheduled"}
                      {report.last_sent && ` | Last sent: ${new Date(report.last_sent).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendReportNow(report.report_id)}
                    disabled={loading}
                    className="rounded-full"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Send Now
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteScheduledReport(report.report_id)}
                    className="rounded-full text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No scheduled reports yet</p>
            <p className="text-sm">Set up automatic weekly or monthly reports</p>
          </div>
        )}
      </Card>

      {/* Schedule Report Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Automatic Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium text-secondary">Frequency</label>
              <Select 
                value={newReport.frequency} 
                onValueChange={(v) => setNewReport({ ...newReport, frequency: v })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newReport.frequency === "weekly" && (
              <div>
                <label className="text-sm font-medium text-secondary">Day of Week</label>
                <Select 
                  value={String(newReport.day_of_week)} 
                  onValueChange={(v) => setNewReport({ ...newReport, day_of_week: parseInt(v) })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Monday</SelectItem>
                    <SelectItem value="1">Tuesday</SelectItem>
                    <SelectItem value="2">Wednesday</SelectItem>
                    <SelectItem value="3">Thursday</SelectItem>
                    <SelectItem value="4">Friday</SelectItem>
                    <SelectItem value="5">Saturday</SelectItem>
                    <SelectItem value="6">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {newReport.frequency === "monthly" && (
              <div>
                <label className="text-sm font-medium text-secondary">Day of Month</label>
                <Select 
                  value={String(newReport.day_of_month)} 
                  onValueChange={(v) => setNewReport({ ...newReport, day_of_month: parseInt(v) })}
                >
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-secondary">Recipient Emails</label>
              <Input
                placeholder="email1@example.com, email2@example.com"
                value={newReport.recipient_emails}
                onChange={(e) => setNewReport({ ...newReport, recipient_emails: e.target.value })}
                className="mt-1 rounded-xl"
              />
              <p className="text-xs text-muted-foreground mt-1">Separate multiple emails with commas</p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newReport.include_patient_details}
                  onChange={(e) => setNewReport({ ...newReport, include_patient_details: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Include patient details</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newReport.include_compliance_summary}
                  onChange={(e) => setNewReport({ ...newReport, include_compliance_summary: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Include compliance summary</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newReport.include_hba1c_trends}
                  onChange={(e) => setNewReport({ ...newReport, include_hba1c_trends: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Include HbA1c trends</span>
              </label>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                onClick={createScheduledReport} 
                disabled={loading}
                className="rounded-full bg-green-600 hover:bg-green-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                Schedule Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerReports;
